import React from 'react';
import { Form, useFormikContext } from 'formik';
import { Button } from '../../Button/Button';
import { Input } from '../../Input/Input';
import { Radio } from '../../Radio/Radio';
import { AddResourceFormValues } from '../types';
import { useResourceFormFields } from '../hooks/useResourceFormFields';
import { ImageUpload } from '../../ImageUpload/ImageUpload';
import { FileAttachment } from '../../FileAttachment/FileAttachment';
import { Select } from '../../Select/Select';

interface ResourceFormFieldsProps {
  onCancel: () => void;
}

export const ResourceFormFields: React.FC<ResourceFormFieldsProps> = ({ onCancel }) => {
  const formik = useFormikContext<AddResourceFormValues>();
  const { agencyOptions, categoryOptions } = useResourceFormFields(formik);

  const handleInputBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    formik.handleBlur(e);
  };

  const handleSelectBlur = (fieldName: string) => {
    formik.setFieldTouched(fieldName, true, true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    Object.keys(formik.values).forEach((key) => {
      formik.setFieldTouched(key, true, true);
    });

    formik.submitForm();
  };

  return (
    <Form noValidate onSubmit={handleSubmit}>
      <ImageUpload isRequired={true} fieldName="thumbnail" />
      <Input
        id="name"
        name="name"
        label="Name"
        placeholder="Enter the name"
        value={formik.values.name}
        onChange={formik.handleChange}
        onBlur={handleInputBlur}
        error={formik.touched.name ? formik.errors.name : undefined}
        touched={formik.touched.name}
      />
      <div className="type-selection">
        <Radio
          label="Type"
          id="type"
          name="type"
          options={[
            { value: 'file', label: 'File' },
            { value: 'link', label: 'Link' },
          ]}
          value={formik.values.type}
          onChange={(e) => {
            formik.handleChange(e);
            formik.setFieldTouched('type', true, true);
          }}
          error={formik.touched.type ? formik.errors.type : undefined}
        />
      </div>
      {formik.values.type === 'file' && <FileAttachment label="Attach file" />}
      {formik.values.type === 'link' && (
        <Input
          id="link"
          name="link"
          label="Link"
          placeholder="Paste the link here"
          value={formik.values.link || ''}
          onChange={formik.handleChange}
          onBlur={handleInputBlur}
          error={formik.touched.link ? formik.errors.link : undefined}
          touched={formik.touched.link}
        />
      )}
      <Select
        id="category"
        name="category"
        label="Category"
        options={categoryOptions}
        value={formik.values.category || ''}
        onChange={(value) => {
          formik.setFieldValue('category', value);
        }}
        onBlur={() => handleSelectBlur('category')}
        error={formik.touched.category ? (formik.errors.category as string) : undefined}
        placeholder="Choose Category"
      />
      <Select
        id="agency"
        name="agency"
        label="Agency"
        options={agencyOptions}
        value={formik.values.agency}
        onChange={(value) => {
          formik.setFieldValue('agency', value);
        }}
        onBlur={() => handleSelectBlur('agency')}
        error={formik.touched.agency ? (formik.errors.agency as string) : undefined}
        placeholder="Choose Agency"
      />
      <div className="form-actions">
        <Button variant="button" className="outline cancel" type="button" onClick={onCancel}>
          Cancel
        </Button>
        <Button variant="button" type="submit">
          Add New Resource
        </Button>
      </div>
    </Form>
  );
};
